import { useStateParams } from "../hooks/useStateParams";
import { TalentOrderByFieldKeys, TalentSummary } from "../models/app/talent";
import { GigApiPaginatedData } from "./common/apiResponse";
import { useGiggedApiSWR } from "./common/giggedApiSWR";
import { SortOrder } from "./common/sortOrder";
import { ITEMS_PER_PAGE } from "./common/pagination";

export type UseTalentsParams = {
    pageSize?: number
}

export type UseTalentsReturn = {
    talents: TalentSummary[]
    isLoading: boolean
    sortOrder: SortOrder
    orderBy: TalentOrderByFieldKeys
    searchTerm: string
    skillIds: string[]
    locationIds: string[]
    languageIds: string[]
    industrialExperienceIds: string[]
    pageIndex: number
    pageSize: number
    totalCount: number
    totalPages: number
    hasPreviousPage: boolean
    hasNextPage: boolean
    setSearchTerm: (value: string) => void
    setSortOrder: (value: SortOrder) => void
    setOrderBy: (value: TalentOrderByFieldKeys) => void
    setSkillIds: (skillIds: string[]) => void
    setLocationIds: (locationIds: string[]) => void
    setLanguageIds: (languageIds: string[]) => void
    setIndustrialExperienceIds: (skillIds: string[]) => void
    setPageIndex: (pageIndex: number) => void
    setPageSize: (pageSize: number) => void
}

export type UseTalents = (params?: UseTalentsParams) => UseTalentsReturn;

export const useTalents: UseTalents = ({
    pageSize: initialPageSize = ITEMS_PER_PAGE,
} = {}) => {
    const [searchTerm, setSearchTerm] = useStateParams("", "searchTerm", "string");
    const [skillIds, setSkillIds] = useStateParams<string[]>([], "skillIds", "stringArray");
    const [locationIds, setLocationIds] = useStateParams<string[]>([], "locationIds", "stringArray");
    const [languageIds, setLanguageIds] = useStateParams<string[]>([], "languageIds", "stringArray");
    const [industrialExperienceIds, setIndustrialExperienceIds] = useStateParams<string[]>([], "industrialExperienceIds", "stringArray");
    const [sortOrder, setSortOrder] = useStateParams<SortOrder>("desc", "sortOrder", "string");
    const [orderBy, setOrderBy] = useStateParams<TalentOrderByFieldKeys>("FirstName", "orderBy", "string");
    const [pageIndex, setPageIndex] = useStateParams<number>(0, "pageIndex", "number");
    const [pageSize, setPageSize] = useStateParams<number>(initialPageSize, "pageSize", "number");

    const searchTermQuery = searchTerm && searchTerm !== "" ? `&searchTerm=${searchTerm}` : "";
    const skillsQuery = skillIds.length > 0 ? `&skillIds=${skillIds.join(",")}` : "";
    const locationsQuery = locationIds.length > 0 ? `&locationIds=${locationIds.join(",")}` : "";
    const languagesQuery = languageIds.length > 0 ? `&languageIds=${languageIds.join(",")}` : "";
    const industrialExperienceQuery = industrialExperienceIds.length > 0 ? `&industrialExperienceIds=${industrialExperienceIds.join(",")}` : "";

    const url = "api/talents?" +
        `pageIndex=${pageIndex}` +
        `&pageSize=${pageSize}` +
        `&sortOrder=${sortOrder}` +
        `&orderBy=${orderBy}` +
        searchTermQuery +
        skillsQuery + 
        locationsQuery +
        industrialExperienceQuery +
        languagesQuery;

    const { data, isLoading } = useGiggedApiSWR<GigApiPaginatedData<TalentSummary>>(url);

    return {
        talents: data?.items || [],
        isLoading,
        orderBy,
        sortOrder,
        searchTerm,
        skillIds,
        locationIds,
        languageIds,
        industrialExperienceIds,
        pageIndex: data?.pageIndex ?? 0,
        pageSize: data?.pageSize ?? pageSize,
        totalCount: data?.totalCount ?? 0,
        totalPages: data?.totalPages ?? 0,
        hasPreviousPage: data?.hasPreviousPage ?? false,
        hasNextPage: data?.hasNextPage ?? false,
        setSearchTerm,
        setOrderBy,
        setSortOrder,
        setSkillIds,
        setLocationIds,
        setLanguageIds,
        setIndustrialExperienceIds,
        setPageIndex,
        setPageSize,
    };
};
